/*
 * CliC — Rx cart values display (RX_FILE_STORAGE_SPEC.md §5 design note / §6,
 * Phase 1, 2026-07-22)
 *
 * The Lens Flow wizard deliberately keeps the typed prescription values OFF the
 * order's line-item properties (visible or `_`-hidden): order-scoped apps ingest
 * the raw order webhook — Klaviyo stores it verbatim — so PHI on the order is
 * the GoodRx/MHMDA sharing pattern. Instead, at add-to-cart the wizard stashes
 * the shopper's condensed display rows in THEIR OWN sessionStorage under
 * `clic_lf_rx_<group>` ({rows: [{label, value}], at}), and this script renders
 * them under the matching lens cart line (`_rx_group` + `_rx_role: "lens"`).
 *
 * Custom Lenses pairs (CUSTOM_LENS_SPEC.md §9.1, 2026-08-26) use the same shape:
 * `_custom_group` + `_custom_role: "lens"` and the stash `clic_lf_custom_<group>`.
 * Their per-eye powers are NOT sensitive and (from lens-flow-80) also ride the lens
 * line as visible props, which the theme renders natively — so a stashed row whose
 * label is already rendered as a property on that line is skipped (no duplicate);
 * the stash only fills the gap for a lens line that lacks the props.
 *
 * Same-device/same-tab-session display only, by design: a cart reopened later
 * (or on another device) shows just the non-PHI "Prescription" selection string.
 * Fail-safe: no paired lines, no stash, or an unreadable /cart.js -> silent no-op.
 * Rows render as .cart__item__property, matching the theme's native property
 * styling (no CSS of its own). Companion of clic-rx-cart-pairing.js (quantity
 * sync) and clic-rx-cart-ack.js (checkout acknowledgment) — one concern each.
 */
(function () {
  "use strict";

  // One entry per made-to-order pair kind: group prop, role prop, stash prefix.
  var PAIR_KINDS = [
    { group: "_rx_group", role: "_rx_role", stash: "clic_lf_rx_" },
    { group: "_custom_group", role: "_custom_role", stash: "clic_lf_custom_" },
  ];
  var MARKER_CLASS = "clic-rx-values";
  var MAX_ROWS = 12; // stash is client data — cap render defensively

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (r) {
      return r.ok ? r.json() : null;
    });
  }

  function readStash(prefix, group) {
    try {
      var raw = sessionStorage.getItem(prefix + group);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !Array.isArray(data.rows) || !data.rows.length) return null;
      return data.rows;
    } catch (e) {
      return null;
    }
  }

  // Render the stashed rows under one line's meta block. Idempotent per render
  // pass: the drawer re-renders its innerHTML on every cart change, wiping old
  // injections; if our marker somehow survived, refresh it in place.
  // Labels the theme already rendered as native properties under this line
  // (".cart__item__property" first span = "Label: ") — stashed rows with the same
  // label are skipped so a lens line carrying the values as props shows them once.
  function renderedLabels(metaEl) {
    var out = {};
    var props = metaEl.querySelectorAll(".cart__item__property > span:first-child");
    for (var i = 0; i < props.length; i++) {
      var t = (props[i].textContent || "").replace(/:\s*$/, "").trim();
      if (t) out[t] = true;
    }
    return out;
  }

  function inject(metaEl, rows) {
    var old = metaEl.querySelector("." + MARKER_CLASS);
    if (old) old.remove();
    var have = renderedLabels(metaEl);
    var wrap = document.createElement("div");
    wrap.className = MARKER_CLASS;
    for (var i = 0; i < rows.length && i < MAX_ROWS; i++) {
      var row = rows[i];
      if (!row || !row.label || row.value == null) continue;
      if (have[String(row.label).trim()]) continue; // already a native property on the line
      var p = document.createElement("p");
      p.className = "cart__item__property";
      var label = document.createElement("span");
      label.textContent = String(row.label) + ": ";
      var value = document.createElement("span");
      value.textContent = String(row.value);
      p.appendChild(label);
      p.appendChild(value);
      wrap.appendChild(p);
    }
    if (wrap.children.length) metaEl.appendChild(wrap);
  }

  var rendering = false;
  function render() {
    if (rendering) return;
    // No rx line elements on the page -> nothing to do without a cart fetch.
    if (!document.querySelector("[data-cart-item]")) return;
    rendering = true;
    getCart()
      .then(function (cart) {
        rendering = false;
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          var props = items[i].properties || {};
          var rows = null;
          for (var k = 0; k < PAIR_KINDS.length && !rows; k++) {
            var kind = PAIR_KINDS[k];
            if (!props[kind.group] || props[kind.role] !== "lens") continue;
            rows = readStash(kind.stash, props[kind.group]);
          }
          if (!rows) continue;
          var lineEls = document.querySelectorAll('[data-cart-item][data-item="' + items[i].key + '"]');
          for (var j = 0; j < lineEls.length; j++) {
            var meta = lineEls[j].querySelector(".cart__item__meta");
            if (meta) inject(meta, rows);
          }
        }
      })
      .catch(function () {
        rendering = false;
      });
  }

  var debounceTimer = null;
  function scheduleRender() {
    if (debounceTimer) clearTimeout(debounceTimer);
    // The drawer swaps its innerHTML after these events fire; give it a beat.
    debounceTimer = setTimeout(render, 180);
  }

  ["theme:cart:update", "theme:cart:added", "theme:cart:refresh", "theme:cart:loaded"].forEach(
    function (ev) { document.addEventListener(ev, scheduleRender); },
  );

  // Catch drawer re-renders that fire no theme:cart:* event (e.g. quantity
  // corrections from the pairing script): watch the drawer subtree. Our own
  // injections re-trigger the observer, but render() is idempotent and the
  // debounce collapses the churn.
  var drawer = document.querySelector("cart-element, [data-cart-drawer]");
  if (drawer && window.MutationObserver) {
    new MutationObserver(scheduleRender).observe(drawer, { childList: true, subtree: true });
  }

  function boot() { render(); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
